// SPDX-FileCopyrightText: Szilárd Hajba
// SPDX-License-Identifier: LGPL-3.0-or-later

/**
 * Media Picker Message Handlers for Shell
 *
 * Handles media picker messages from apps using ACK + push pattern:
 * - media:pick.req - App requests to open the media picker
 * - media:pick.ack - Shell immediately acknowledges dialog is opening (short timeout)
 * - media:pick.result - Shell pushes result when user completes/cancels (no timeout)
 *
 * This pattern allows users unlimited time to browse/upload media without
 * the request timing out.
 */

import type { MediaPickReq } from '@cloudillo/core'

import { registerPendingTempId } from '../../services/file-id-resolver.js'
import type { ShellMessageBus } from '../shell-bus.js'
import { idTagFromResId } from './resId.js'

/**
 * Media picker options passed to the component
 */
export interface MediaPickerOpenOptions {
	mediaType?: string
	documentVisibility?: 'P' | 'C' | 'F'
	documentFileId?: string
	requirePublic?: boolean // Site source: only Public files may be picked
	enableCrop?: boolean
	cropAspects?: Array<'16:9' | '4:3' | '3:2' | '1:1' | 'circle' | 'free'>
	title?: string
	isExternalContext?: boolean // True when opened from external app via bus
	idTag?: string // Document's context idTag (from app connection)
}

/**
 * Media picker result from the component
 */
export interface MediaPickerResultData {
	fileId: string
	fileName: string
	contentType: string
	dim?: [number, number]
	visibility?: 'P' | 'C' | 'F'
	visibilityAcknowledged?: boolean
	croppedVariantId?: string
}

/**
 * Callback type for opening the media picker
 * Called by the handler when an app requests the media picker
 */
export type MediaPickerCallback = (
	options: MediaPickerOpenOptions,
	onResult: (result: MediaPickerResultData | null) => void
) => void

// Callback set by the MediaPicker component
let openMediaPickerCallback: MediaPickerCallback | null = null

/**
 * Register the media picker callback
 * Called by the MediaPicker component when it mounts
 */
export function setMediaPickerCallback(callback: MediaPickerCallback | null): void {
	openMediaPickerCallback = callback
}

/**
 * Initialize media message handlers on the shell bus
 */
export function initMediaHandlers(bus: ShellMessageBus): void {
	// Handle media pick request from apps using ACK + push pattern
	bus.on('media:pick.req', async (msg: MediaPickReq, source) => {
		const appWindow = source as Window
		if (!appWindow) {
			console.error('[Media] Pick request with no source window')
			return
		}

		const sessionId = msg.payload.sessionId

		const connection = bus.getAppTracker().validateSource(source, true)
		if (!connection) {
			console.warn('[Media] Pick request from uninitialized/unknown app')
			// Send error ACK (not result - ACK handles errors during dialog opening)
			bus.sendResponse(
				appWindow,
				'media:pick.ack',
				msg.id,
				false,
				undefined,
				'App not initialized'
			)
			return
		}

		// Defence in depth behind the dispatch gate; see EMBED_ALLOWED_MESSAGES in shell-bus.ts.
		if (connection.embed) {
			console.warn('[Media] Pick request from an embed connection')
			bus.sendResponse(
				appWindow,
				'media:pick.ack',
				msg.id,
				false,
				undefined,
				'Cannot pick media from an embedded document'
			)
			return
		}

		if (!openMediaPickerCallback) {
			console.error('[Media] No media picker callback registered')
			// Send error ACK
			bus.sendResponse(
				appWindow,
				'media:pick.ack',
				msg.id,
				false,
				undefined,
				'Media picker not available'
			)
			return
		}

		console.log(
			'[Media] Opening media picker for app:',
			connection.appName,
			'sessionId:',
			sessionId
		)

		// Send ACK immediately - dialog is opening
		// This allows the app to know the dialog is opening without timing out
		bus.sendResponse(appWindow, 'media:pick.ack', msg.id, true, { sessionId })

		const contextIdTag = idTagFromResId(connection.resId) || connection.idTag

		// Open the modal and wait for result
		// When result arrives, send push notification (no timeout on user interaction)
		openMediaPickerCallback(
			{
				mediaType: msg.payload.mediaType,
				documentVisibility: msg.payload.documentVisibility,
				documentFileId: msg.payload.documentFileId,
				requirePublic: msg.payload.requirePublic,
				enableCrop: msg.payload.enableCrop,
				cropAspects: msg.payload.cropAspects,
				title: msg.payload.title,
				isExternalContext: true, // Always true when opened via bus protocol
				idTag: contextIdTag // Document's context idTag from resId
			},
			(result) => {
				if (result) {
					console.log('[Media] Media picker result:', result)

					// Register pending temp ID if it's a temporary ID (starts with @)
					// This enables resolution when FILE_ID_GENERATED event arrives
					if (result.fileId.startsWith('@')) {
						registerPendingTempId(result.fileId, appWindow)
					}

					// Send result as push notification (not response)
					bus.sendNotify(appWindow, 'media:pick.result', {
						sessionId,
						selected: true,
						fileId: result.fileId,
						fileName: result.fileName,
						contentType: result.contentType,
						dim: result.dim,
						visibility: result.visibility,
						visibilityAcknowledged: result.visibilityAcknowledged,
						croppedVariantId: result.croppedVariantId
					})
				} else {
					console.log('[Media] Media picker cancelled')
					// Send cancelled result as push notification
					bus.sendNotify(appWindow, 'media:pick.result', {
						sessionId,
						selected: false
					})
				}
			}
		)
	})
}

// vim: ts=4
